// "Edit on my site" drives a page the customer opens themselves in a second
// window: nothing is framed, so there is no postMessage channel — the SDK on
// that page polls the draft the admin writes. See the layout edit block in
// releva-sdk-js.js for the other half of this contract.
export const EDIT_MODE = 'layout-edit';

// Placing a block on the page instead of editing one that is already there:
// the same second-window arrangement, in a mode where the SDK lets the merchant
// drop the block somewhere and reports the selector back on the session rather
// than rendering a draft.
export const PLACEMENT_EDIT_MODE = 'placement-edit';

// A search-as-you-type layout is not placed on a page: it renders in the
// dropdown of whatever search box the shop has, wired up through the domain's
// search config rather than through a placement. The SDK drives it from a mode
// of its own for that reason.
export const SEARCH_EDIT_MODE = 'search-edit';

// Looking at the shop as one of its customers. Nothing is edited and no draft
// is streamed — the SDK simply personalises the page for the profile the
// session names, which is why this is a mode of its own rather than an option
// on an edit session.
export const IMPERSONATE_MODE = 'impersonate';

// Swaps the mode on a url that is already pointing at the right page. Only
// http(s) is accepted: everywhere this is reached from user-typed text (the
// "Page to search on" field), a javascript:/data:/mailto: string would
// otherwise parse cleanly and end up assigned to a window's location.
export const withPreviewMode = (url, mode) => {
  try {
    const parsed = new URL(url);

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }

    parsed.searchParams.set('rlv_mode', mode);
    return parsed.href;
  } catch {
    return null;
  }
};

// Builds the url to open from the domain's site url and a page's pathname. Both
// come from the API and either can be missing or messy, so this resolves through
// the URL parser rather than by concatenation. An empty pathname means the
// domain root.
export const buildPreviewUrl = (siteUrl, pathname, mode) => {
  if (!siteUrl) {
    return null;
  }

  try {
    return withPreviewMode(new URL(pathname || '/', siteUrl).href, mode);
  } catch {
    return null;
  }
};

// A session token goes in the fragment, never the query string: a query reaches
// the shop's server, its logs, its analytics and any referrer it sends onwards,
// while a fragment stays in the browser where the SDK reads it.
//
// Every session url has that same shape — the mode in the query, the token in
// the fragment under a key naming which kind of session it is, and whatever else
// that kind carries beside it. Only those extras are dropped when blank; the
// token's own parameter is always written, so a caller with nothing to put in it
// builds a plainly unusable session url rather than one that looks token-less.
const buildSessionUrl = (pageUrl, mode, tokenKey, token, extras) => {
  try {
    const url = new URL(withPreviewMode(pageUrl, mode));
    const fragment = [`${tokenKey}=${encodeURIComponent(token)}`];

    for (const [key, value] of Object.entries(extras)) {
      if (value) {
        fragment.push(`${key}=${encodeURIComponent(value)}`);
      }
    }

    url.hash = fragment.join('&');
    return url.href;
  } catch {
    return null;
  }
};

// The url the customer's own window is sent to. A search session's seed query
// rides along beside the token, so the page opens on a populated dropdown.
//
// `returnUrl` is where the page's Done button sends the merchant back to. It
// rides in the fragment for the same reason the token does: it names an admin
// account, domain and block, which is no business of the shop's server. Only the
// placement flow sends one, because only it navigates the merchant's own tab away.
export const buildEditUrl = (pageUrl, token, { mode = EDIT_MODE, query = '', returnUrl = '' } = {}) =>
  buildSessionUrl(pageUrl, mode, 'rlv_edit', token, { rlv_q: query, rlv_return: returnUrl });

// The url the operator's own new tab is sent to, to see the shop as one of its
// customers. Which customer that is lives on the session the token names and
// nowhere in the url: the profile id used to travel in the query, where anyone
// holding the shop's public token could retype it for any other profile.
//
// A distinct fragment key rather than `rlv_edit`, so the SDK can tell an
// impersonation session from an edit session before it validates either.
export const buildImpersonateUrl = (pageUrl, token, { returnUrl = '' } = {}) =>
  buildSessionUrl(pageUrl, IMPERSONATE_MODE, 'rlv_impersonate', token, { rlv_return: returnUrl });

// The site a session is running against, short enough to name in the editor's
// status line without repeating the whole url back at the user.
export const originOf = (url) => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

// The pages an edit session can drive, one entry per page: a session drives a
// whole page, so two blocks of this layout on the same page are one session.
// A switched-off block still counts — the SDK's edit mode asks the API for those
// too, which is the point of editing a block before turning it on.
export const editablePagesOf = (template) => {
  const placed = (template?.recommenders || []).filter((recommender) => recommender.cssSelector && recommender.pageId);

  return placed
    .filter((recommender, index) => placed.findIndex((other) => other.pageId === recommender.pageId) === index)
    .map((recommender) => ({
      pageId: recommender.pageId,
      name: recommender.page?.name || 'Page',
      pathname: recommender.page?.pathname || '',
    }));
};
